import { useContext, useEffect, useState } from 'react';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { useParams, useLocation } from "react-router";
import 'bootstrap/dist/css/bootstrap.min.css';
import LocalizedStrings from 'react-localization';

import { ListView } from "./ListView";
import { JobService, JobForList, JobList, EmploymentType, getEmploymentTypeAsString, SortingCriteria, getSortingCriteria, Job, SettingsService, ClientLanguage, getClientLanguageAsString } from '../Api';
import { PendingView } from './PendingView';
import { ErrorView } from './ErrorView';
import { EmptyJobListView } from './EmptyJobListView';
import { NotExistingJobListView } from './NotExistingJobListView';
import { LoadingStates } from './Enums';
import { CustomSelect, CustomFormControl, CustomBlockButton } from './StyledComponents';
import SettingsContext from '../SettingsContext';
import { addCss, addFont } from '../Utils';

let locStrings = new LocalizedStrings({
    de:
    {
        messageJobPostingsLoaded:"Stellenanzeigen werden geladen...",
        messageErrorLoadingJobPostings:"Beim Abrufen der Stellenanzeigen ist ein Fehler aufgetreten.",
        messageErrorLoadingJobPostingsDescriptionInformal:"Wende dich bitte an deinen Systemadministrator oder probiere es zu einem späteren Zeitpunkt nochmal.",
        messageErrorLoadingJobPostingsDescriptionFormal:"Wenden Sie sich bitte an den Systemadministrator oder probieren Sie es zu einem späteren Zeitpunkt nochmal.",
        messageJobPostingsNotFound:"Keine Stellenanzeigen zu den ausgewählten Kriterien gefunden.",
        labelJobFilterInput:"Suchbegriff:",
        buttonTextResetJobFilterInput:"Zurücksetzen",
        labelListFilterEmploymentType:"Anstellungsart",
        labelListFilterGrouping:"Gruppierung",
        labelListFilterExperienceLevel:"Karrierelevel",
        labelListFilterLocation:"Standort",
        labelListFilterSorting:"Sortierung",
    },
    en: 
    {
        messageJobPostingsLoaded:"Job postings are loaded...",
        messageErrorLoadingJobPostings:"An error occurred during loading of job postings.",
        messageErrorLoadingJobPostingsDescriptionInformal:"Please contact your system administrator or try again later.",
        messageErrorLoadingJobPostingsDescriptionFormal:"Please contact your system administrator or try again later.",
        messageJobPostingsNotFound:"No job postings found for the selected criteria.",
        labelJobFilterInput:"Search term:",
        buttonTextResetJobFilterInput:"Reset",
        labelListFilterEmploymentType:"Type of employment",
        labelListFilterGrouping:"Grouping",
        labelListFilterExperienceLevel:"Career level",
        labelListFilterLocation:"Location",
        labelListFilterSorting:"Sorting",
    }
});

export const JobsView = () => {
    const { pathname } = useLocation();
    let { tenant = "", listName } = useParams();
    tenant = tenant === "" ? pathname.split('/')[1] : tenant; 
    const [jobList, setJobList] = useState<JobList>()
    const [loadingState, setLoadingState] = useState<LoadingStates>(LoadingStates.Pending)
    const [location, setLocation] = useState(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
    const [employmentType, setEmploymentType] = useState<EmploymentType>(EmploymentType.None);
    const [experienceLevel, setExperienceLevel] = useState(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
    const [grouping, setGrouping] = useState(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
    const [sorting, setSorting] = useState(getSortingCriteria(SortingCriteria.None, jobList?.clientLanguage));
    const [jobFilterText, setJobFilterText] = useState("");
    const { isLoading, settings } = useContext(SettingsContext);

    var locations = [] as string[];
    var employmentTypes = [] as EmploymentType[];
    var experienceLevels = [] as string[];
    var groupings = [] as string[];
    var sortingCriteria = [] as string[];
    var fallbackExperienceLevel = false as boolean;  // true: use field job.experienceLevel; false: use field job.experienceLevels

    if (jobList) {
        setLanguage();
    } 

    useEffect(() => {
        if (tenant && listName) {
            JobService.getJobs(tenant, listName, true).then(result => {
                if (result) {
                    setJobList(result);
                    setLoadingState(result.jobs?.length > 0 ? LoadingStates.Success : LoadingStates.EmptyResult);
                    setLocation(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
                    setExperienceLevel(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
                    setGrouping(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
                    setSorting(getSortingCriteria(SortingCriteria.None, jobList?.clientLanguage));
                }
                else {
                    setLoadingState(LoadingStates.NotExistingList)
                }
            }).catch(() => {
                setLoadingState(LoadingStates.Fail)
            });
            document.title = tenant + " -  " + listName;
        }
        
    }, [listName, tenant])

    const getGroupedJobList = () => {
        const filteredJobs = jobList?.jobs.filter(
            job =>
                (location === getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage) || job.locations?.includes(location) ) &&
                (employmentType === EmploymentType.None || job.employmentTypes.includes(employmentType)) &&
                (experienceLevel === getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage) || (!fallbackExperienceLevel && job.experienceLevels?.includes(experienceLevel)) || (fallbackExperienceLevel && job.experienceLevel === experienceLevel)) &&
                (jobFilterText === "" || job.title.toLowerCase().includes(jobFilterText.toLowerCase())) &&
                (grouping === getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage) || job.group === grouping));

        if (sorting !== getSortingCriteria(SortingCriteria.None, jobList?.clientLanguage)) {
            const jobs = new Map();
            if (sorting === getSortingCriteria(SortingCriteria.Alphabetical, jobList?.clientLanguage)) {
                jobs.set('', filteredJobs?.sort(sortByName));
            } else {
                jobs.set('', filteredJobs?.sort(sortByPublishedAt));
            }
            return jobs;
        } else {
            return groupBy(filteredJobs, job => job.group);
        }
    }

    function changeFavicon():void {
        const link = document.getElementById('favicon');
        link?.setAttribute('href',settings.imagesUrl+ "/" + tenant + "." + jobList?.favIconKey);
    }

    function sortByName(jobA: Job, jobB: Job): number {
        if (jobA.title < jobB.title) return -1;
        if (jobA.title > jobB.title) return 1;
        return 0;
    }

    function sortByPublishedAt(jobA: Job, jobB: Job): number {
        if (jobA.publishedAt === jobB.publishedAt) return 0;
        if (jobA.publishedAt === null || jobA.publishedAt === undefined) return 1;
        if (jobB.publishedAt === null || jobB.publishedAt === undefined) return -1;

        const d1 = new Date(jobA.publishedAt);
        const d2 = new Date(jobB.publishedAt);

        return d1 < d2 ? 1 : -1;
    }

    function initFilterValues(jobList: JobList): void {
        if (jobList.jobs.length > 0 && jobList.jobs[0].experienceLevels === null) {
            fallbackExperienceLevel = true;
        }
        else {
            fallbackExperienceLevel = false;
        }
        jobList.jobs.forEach((job) => {
            job.locations?.forEach((item) => {
                if (!locations.includes(item)) {
                    locations.push(item);
                }
            })
            job.employmentTypes.forEach((type) => {
                if (!employmentTypes.includes(type)) {
                    employmentTypes.push(type);
                }
            })
            if (!fallbackExperienceLevel) {
                if (Array.isArray(job.experienceLevels) && job.experienceLevels.length > 0){
                    job.experienceLevels?.forEach((item) => {
                        if (!experienceLevels.includes(item)) {
                            if (item !== '') {
                                experienceLevels.push(item);
                            }
                        }
                    })
                }
            }
            else {
                if (job.experienceLevel !== null) {
                    if (!experienceLevels.includes(job.experienceLevel)) {
                        if (job.experienceLevel !== '') {
                            experienceLevels.push(job.experienceLevel);
                        }
                    }
                }
            }
            if (!groupings.includes(job.group)) {
                if (job.group !== '') {
                    groupings.push(job.group);
                }
            }
        })

        // leere Einträge hinzufügen, um alle Stellen anzuzeigen
        if (!locations.includes(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage))) {
            locations.push(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
            locations.sort();
        }
        if (!employmentTypes.includes(EmploymentType.None)) {
            employmentTypes.sort(); // Vorher sortieren
            employmentTypes.splice(0, 0, EmploymentType.None);
        }
        if (!experienceLevels.includes(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage))) {
            experienceLevels.sort();
            experienceLevels.splice(0, 0, getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
        }
        if (!groupings.includes(getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage))) {
            groupings.sort();
            groupings.splice(0, 0, getSortingCriteria(SortingCriteria.FilterAll, jobList?.clientLanguage));
        }
    }

    function initSortingValues() {
        sortingCriteria.push(...[
            getSortingCriteria(SortingCriteria.None, jobList?.clientLanguage),
            getSortingCriteria(SortingCriteria.Alphabetical, jobList?.clientLanguage),
            getSortingCriteria(SortingCriteria.NewestFirst, jobList?.clientLanguage),
        ]);
    }

    const getFont = () => {
        if (jobList?.fontAsBase64) {
            const fontFamily = addFont(jobList.fontKey, jobList.fontAsBase64);
            return fontFamily;
        }
        return jobList?.fontFamily;
    };

    const onResetJobFilter = () => {
        setJobFilterText("");
    }

    function getClientLanguage(): string { 
        if (jobList?.clientLanguage) 
            return getClientLanguageAsString(jobList?.clientLanguage); 
        else 
            return ""; 
    } 

    function setLanguage(): void {
        switch(jobList?.clientLanguage) { 
            case ClientLanguage.German: { 
               locStrings.setLanguage('de');
               break; 
            } 
            case ClientLanguage.English: { 
               locStrings.setLanguage('en'); 
               break; 
            } 
            // Otherwise "jobList?.clientLanguage" is "ClientLanguage.BrowserSetting" and language used is browser setting dependent.
        } 
    }

    return (
        <>
            {!isLoading &&
                <>
                    {jobList?.cssKey && addCss(`${settings.imagesUrl}/${tenant}.${jobList.cssKey}`)  }
                    {jobList && setLanguage()}
                    {jobList && changeFavicon()}
                    <Container style={{ display: "block", width: "auto",marginTop: "30px", fontFamily: getFont() }}>
                        {loadingState === LoadingStates.Success && jobList && initFilterValues(jobList)}
                        {loadingState === LoadingStates.Success && jobList && initSortingValues()}
                        {loadingState === LoadingStates.Success && jobList &&
                            <Row>
                                {jobList.showEmploymentTypes &&
                                    <Col  >
                                        <Form.Group >
                                            <Form.Label className={"ListFilterEmploymentTypeText"} style={{ paddingLeft: "5px" }}>{locStrings.labelListFilterEmploymentType}:</Form.Label>
                                            <CustomSelect className={"ListFilterEmploymentTypeValues"} value={employmentType} onChange={(event) => setEmploymentType(event.target.value)} colors={jobList.colors}>
                                                {employmentTypes.map((employmentType, index) =>
                                                    <option className={`ListFilterEmploymentTypeOption${index}`} key={employmentType} value={employmentType}>{getEmploymentTypeAsString(employmentType, jobList?.clientLanguage)}</option>)
                                                }
                                            </CustomSelect>
                                        </Form.Group>
                                    </Col>
                                }
                                {jobList.showGroupings  &&
                                    <Col  >
                                        <Form.Group  >
                                            <Form.Label className={"ListFilterGroupingText"} style={{ paddingLeft: "5px" }}>{locStrings.labelListFilterGrouping}:</Form.Label>
                                            <CustomSelect className={"ListFilterGroupingValues"} value={grouping} onChange={(event) => setGrouping(event.target.value)} colors={jobList.colors}>
                                                {groupings.map((grouping, index) =>
                                                    <option className={`ListFilterGroupingOption${index}`} key={grouping}>{grouping}</option>)
                                                }
                                            </CustomSelect>
                                        </Form.Group>
                                    </Col>
                                }
                                {jobList.showExperienceLevels &&
                                    <Col  >
                                        <Form.Group  >
                                            <Form.Label className={"ListFilterExperienceLevelText"} style={{ paddingLeft: "5px" }}>{locStrings.labelListFilterExperienceLevel}:</Form.Label>
                                            <CustomSelect className={"ListFilterExperienceLevelValues"} value={experienceLevel} onChange={(event) => setExperienceLevel(event.target.value)} colors={jobList.colors}>
                                                {experienceLevels.map((experienceLevel, index) =>
                                                    <option className={`ListFilterExperienceLevelOption${index}`} key={experienceLevel}>{experienceLevel}</option>)
                                                }
                                            </CustomSelect>
                                        </Form.Group>
                                    </Col>
                                }
                                {jobList.showLocation &&
                                    <Col  >
                                        <Form.Group >
                                            <Form.Label className={"ListFilterLocationText"} style={{ paddingLeft: "5px" }}>{locStrings.labelListFilterLocation}:</Form.Label>
                                            <CustomSelect className={"ListFilterLocationValues"} value={location} onChange={(event) => setLocation(event.target.value)} colors={jobList.colors}>
                                                {locations.map((location, index) =>
                                                    <option className={`ListFilterLocationOption${index}`} key={location}>{location}</option>)
                                                }
                                            </CustomSelect>
                                        </Form.Group>
                                    </Col>
                                }
                                {jobList.showSorting &&
                                    <Col  >
                                        <Form.Group>
                                            <Form.Label className={"ListFilterSortingText"} style={{ paddingLeft: "5px" }}>{locStrings.labelListFilterSorting}:</Form.Label>
                                            <CustomSelect className={"ListFilterSortingValues"} value={sorting} onChange={(event) => setSorting(event.target.value)} colors={jobList.colors}>
                                                {sortingCriteria.map((sorting, index) =>
                                                    <option className={`ListFilterSortingOption${index}`} key={sorting}>{sorting}</option>)
                                                }
                                            </CustomSelect>
                                        </Form.Group>
                                    </Col>
                                }
                            </Row>}
                        {loadingState === LoadingStates.Success && jobList && !jobList?.hideJobFilter  &&
                            <Row>
                                <Col lg={10}>
                                    <Form.Group className="mb-3" controlId="formJobFilterInput">
                                        <Form.Label style={{ paddingTop: "10px" }} className={"FormJobFilterInputText"}>{locStrings.labelJobFilterInput}</Form.Label>
                                        <CustomFormControl
                                            colors={jobList.colors}
                                            placeholder={""}
                                            type="text"
                                            name="jobFilterInput"
                                            value={jobFilterText}
                                            onChange={(event) => setJobFilterText(event.target.value)}
                                            maxLength={settings.textInputLimit}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col lg={2}>
                                    <div className={"FormJobFilterResetButton"} style={{ paddingTop: "40px", display: "flex", justifyContent: "left" }}>
                                        <CustomBlockButton onClick={onResetJobFilter} colors={jobList.colors}>{locStrings.buttonTextResetJobFilterInput}</CustomBlockButton>
                                    </div>
                                </Col>
                            </Row>}
                        <Row>
                            <Col lg={12}>
                                {loadingState === LoadingStates.Pending && <PendingView message={locStrings.messageJobPostingsLoaded}  clientLanguage={jobList?.clientLanguage}/>}
                                {loadingState === LoadingStates.Fail && !jobList?.formalAddressPronoun && <ErrorView message={locStrings.messageErrorLoadingJobPostings} description={locStrings.messageErrorLoadingJobPostingsDescriptionInformal} />}
                                {loadingState === LoadingStates.Fail && jobList?.formalAddressPronoun && <ErrorView message={locStrings.messageErrorLoadingJobPostings} description={locStrings.messageErrorLoadingJobPostingsDescriptionFormal} />}
                                {loadingState === LoadingStates.Success && jobList && getGroupedJobList().size === 0 && <ErrorView message={locStrings.messageJobPostingsNotFound} />}
                                {loadingState === LoadingStates.Success && jobList && getGroupedJobList().size > 0 &&
                                    <ListView jobList={jobList} tenant={tenant} groupedJobList={getGroupedJobList()}></ListView>}
                                {loadingState === LoadingStates.EmptyResult && <EmptyJobListView clientLanguage={jobList?.clientLanguage}></EmptyJobListView>}
                                {loadingState === LoadingStates.NotExistingList && <NotExistingJobListView clientLanguage={jobList?.clientLanguage}></NotExistingJobListView>}

                            </Col>
                        </Row>
                    </Container>
                    {/* <div>
                        {<small><b>Debug info: ClientLanguage: </b> {getClientLanguage()}</small>}
                    </div> */}
                </>
            }
        </>
    );
}

const groupBy = (list, keyGetter) => {
    const map = new Map<string, JobForList[]>();
    list.forEach((item) => {
        const key = keyGetter(item);
        const collection = map.get(key);
        if (!collection) {
            map.set(key, [item]);
        } else {
            collection.push(item);
        }
    });
    return map;
}

// const getQueryVariable = (variable: string) => {
//     var query = window.location.search.substring(1);
//     var vars = query.split("&");
//     for (var i = 0; i < vars.length; i++) {
//         var pair = vars[i].split("=");
//         if (pair[0] === variable) {
//             return pair[1];
//         }
//     }
//     return undefined;
// }
